import { API_TYPES, RequirementWorkflowType } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { type UseGetQueryOptions } from "@src/appV2/api";
import { useWorkerQualifications } from "@src/appV2/Worker/useWorkerQualifications";
import { type AxiosError } from "axios";
import { useMemo } from "react";

import { type WorkerRequirement } from "../../Documents/api/types";
import { useGetWorkerRequirements } from "../../Documents/api/useGetWorkerRequirements";

interface UseGetPersonalIdRequirementParams {
  options?: Pick<UseGetQueryOptions<any, AxiosError, WorkerRequirement[]>, "refetchInterval">;
}

export function useGetPersonalIdRequirement({ options }: UseGetPersonalIdRequirementParams = {}) {
  const {
    data: qualifications,
    isLoading: isLoadingQualifications,
    isFetching: isFetchingQualifications,
    isSuccess: isSuccessQualifications,
    isError: isErrorQualifications,
    error: qualificationsError,
    refetch: refetchQualifications,
  } = useWorkerQualifications();
  const isWorkerRequirementsQueryEnabled = qualifications.length > 0;

  const {
    data: workerRequirements,
    isLoading: isLoadingWorkerRequirements,
    isFetching: isFetchingWorkerRequirements,
    isSuccess: isSuccessWorkerRequirements,
    isError: isErrorWorkerRequirements,
    error: workerRequirementsError,
    refetch: refetchWorkerRequirements,
  } = useGetWorkerRequirements(
    {
      filter: {
        qualifications: qualifications.map((qualification) => qualification.attributes.name),
        requirementWorkflowType: {
          in: [RequirementWorkflowType.PERSONAL_ID_STRIPE_VERIFICATION],
        },
      },
      // We need to include documents to get automated name updates, since the automated name update is tied to the document rather than the requirement. Therefore, it only gets returned when documents are included.
      include: [API_TYPES.requirements, API_TYPES.workerDocuments, API_TYPES.automatedNameUpdate],
    },
    { ...options, enabled: isWorkerRequirementsQueryEnabled }
  );
  const personalIdRequirement = workerRequirements?.find(
    (item) =>
      item.requirement?.attributes.requirementWorkflowType ===
      RequirementWorkflowType.PERSONAL_ID_STRIPE_VERIFICATION
  );

  // This is used to determine if the personal id is required or not
  // In production we have Personal ID requirement mapped for all qualifications
  // This acts as a fallback to handle cases where Personal ID requirement is not mapped for the selected qualifications
  const isPersonalIdRequired = useMemo(() => {
    // Personal ID requirement can't be fetched if there are no qualifications
    if (isSuccessQualifications && !isWorkerRequirementsQueryEnabled) {
      return false;
    }

    // Personal ID requirement won't be returned if it's not mapped for the selected qualifications
    if (isSuccessWorkerRequirements && !isDefined(personalIdRequirement)) {
      return false;
    }

    return undefined; // undetermined
  }, [
    isSuccessQualifications,
    isWorkerRequirementsQueryEnabled,
    isSuccessWorkerRequirements,
    personalIdRequirement,
  ]);

  async function refetch() {
    await refetchQualifications();
    await refetchWorkerRequirements();
  }

  const isLoading =
    isLoadingQualifications || (isWorkerRequirementsQueryEnabled && isLoadingWorkerRequirements);

  return {
    isLoading,
    personalIdRequirement,
    isPersonalIdRequired,
    isFetching: isFetchingQualifications || isFetchingWorkerRequirements,
    isError: isErrorQualifications || isErrorWorkerRequirements,
    qualificationsError,
    workerRequirementsError,
    refetch,
  };
}
